import { computed, effect, inject, Injectable, linkedSignal, signal, untracked } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { RaxBFF } from '@raxapi/booker.planner.bff.api';
import { ActiveTenantLocationStore } from '@raxapp/multi-tenants/ActiveTenantLocation.store';
import { filter, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

interface ConnectionLocationId {
    locationId?: string;
    prevLocationId?: string;
}

@Injectable({
    providedIn: 'root',
})
export class LocationLiveUpdateSignalRService {
    private activeTenantLocationStore = inject(ActiveTenantLocationStore);

    private _messages = new Subject<RaxBFF.LocationLiveUpdateMessage>();
    public readonly messages$: Observable<RaxBFF.LocationLiveUpdateMessage> = this._messages
        .asObservable()
        .pipe(filter((message) => message.locationId === this.connectionLocationId().locationId));

    private hubConnection!: signalR.HubConnection;
    public isConnected = signal(false);

    private activeLocationId = computed(() => this.activeTenantLocationStore.activeLocation()?.locationId);

    private connectionLocationId = linkedSignal<string | undefined, ConnectionLocationId>({
        source: this.activeLocationId,
        computation: (newActiveLocationId, prev) => {
            return {
                locationId: newActiveLocationId,
                prevLocationId: prev?.value?.locationId,
            };
        },
    });

    constructor() {
        effect(() => {
            const connectionLocationId = this.connectionLocationId();
            const isConnected = this.isConnected();
            if (isConnected === false || connectionLocationId.locationId === connectionLocationId.prevLocationId) {
                return;
            }
            const { locationId, prevLocationId } = connectionLocationId;
            if (prevLocationId) {
                untracked(() => this.unsubscribeFromLocationHub(prevLocationId));
            }
            if (locationId) {
                untracked(() => this.subscribeToLocationHub(locationId));
            }
        });

        this.startConnection();
        this.addListeners();
    }

    private startConnection(): void {
        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(environment.bffSignalApiUrl + '/plannerlocationhub')
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        this.hubConnection
            .start()
            .then(() => {
                console.log('SignalR connection started');
                this.isConnected.set(true);
            })
            .catch((err) => {
                console.error('Error while starting SignalR connection:', err);
                this.isConnected.set(false);
            });

        this.hubConnection.onclose(() => {
            console.warn('SignalR connection closed');
            this.isConnected.set(false);
        });

        this.hubConnection.onreconnected(() => {
            console.log('SignalR reconnected');
            this.isConnected.set(true);
        });

        this.hubConnection.onreconnecting(() => {
            console.log('SignalR reconnecting...');
            this.isConnected.set(false);
        });
    }

    private addListeners(): void {
        this.hubConnection.on('LocationLiveUpdateMessageStream', (message: RaxBFF.LocationLiveUpdateMessage) => {
            this._messages.next(message);
        });
    }

    private subscribeToLocationHub(locationId: string): void {
        this.hubConnection
            .invoke('SubscribeToLocationHub', locationId)
            .catch((err) => console.error('subscribeToLocationHub Error sending message:', err));
    }

    private unsubscribeFromLocationHub(locationId: string): void {
        this.hubConnection
            .invoke('UnsubscribeFromLocationHub', locationId)
            .catch((err) => console.error('unsubscribeFromLocationHub Error sending message:', err));
    }
}
